import React, { useState } from 'react';
import { MessageSquare, Loader2, Eye, Pencil, FileDown, Trash2 } from 'lucide-react';
import { FeedbackRequest } from '../types/feedback';
import { getStatusColor, getStatusIcon } from '../utils/feedbackUtils';
import { Pagination } from './common/Pagination';
import { formatDateInIST } from '../utils/mappers';

const ITEMS_PER_PAGE = 20;

interface RequestsTableProps {
  requests: FeedbackRequest[];
  onViewRequest: (request: FeedbackRequest) => void;
  onEditRequest?: (request: FeedbackRequest) => void;
  onExportPDF?: (request: FeedbackRequest) => void;
  onDeleteRequest?: (request: FeedbackRequest) => void;
  loading?: boolean;
}

const RequestsTable: React.FC<RequestsTableProps> = ({ requests, onViewRequest, onEditRequest, onExportPDF, onDeleteRequest, loading = false }) => {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(requests.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedRequests = requests.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Reset to page 1 when requests change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [requests.length]);

  const canEdit = (status: string) => status === 'draft' || status === 'revision_requested';
  const canExport = (status: string) => status === 'completed';
  const canDelete = (status: string) => status === 'draft';

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-200">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-6">Connect</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-6">Manager</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-6">Date</th>
              <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-6">Status</th>
              <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-6">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-6 py-16 text-center">
                  <div className="flex flex-col items-center gap-4">
                    <Loader2 className="w-10 h-10 text-pink-500 animate-spin" />
                    <p className="text-slate-600 font-medium">Loading feedback requests...</p>
                  </div>
                </td>
              </tr>
            ) : paginatedRequests.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center">
                  <div className="text-slate-400">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-40" />
                    <p className="font-bold text-lg">No requests found</p>
                    <p className="text-sm mt-1">Try adjusting your filters</p>
                  </div>
                </td>
              </tr>
            ) : (
              paginatedRequests.map((request) => (
                <tr
                  key={request.id}
                  className="hover:bg-pink-50/50 transition-colors"
                >
                  <td className="px-6 py-4 align-middle">
                    <div className="flex items-center gap-2">
                      <div className="font-semibold text-slate-900">{request.connectName || request.employeeName}</div>
                      {request.pip?.isOnPip && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wide text-orange-700 bg-orange-50 border border-orange-200">
                          PIP
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 align-middle text-slate-600 text-sm">{request.managerName}</td>
                  <td className="px-6 py-4 align-middle text-slate-500 text-sm">
                    {formatDateInIST(request.requestDate)}
                  </td>
                  <td className="px-6 py-4 align-middle text-center">
                    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border whitespace-nowrap ${getStatusColor(request.status)}`}>
                      {getStatusIcon(request.status)}
                      {request.status.replace('_', ' ').toUpperCase()}
                    </div>
                  </td>
                  <td className="px-6 py-4 align-middle text-center">
                    <div className="inline-flex items-center gap-1.5">
                      {/* View — for non-editable statuses */}
                      {!canEdit(request.status) && (
                        <button
                          onClick={() => onViewRequest(request)}
                          title="View"
                          className="p-2 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-lg transition-all shadow-sm shadow-pink-500/20"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      )}
                      {/* Edit — for draft and revision_requested */}
                      {canEdit(request.status) && onEditRequest && (
                        <button
                          onClick={() => onEditRequest(request)}
                          title="Edit"
                          className="p-2 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-lg transition-all shadow-sm shadow-pink-500/20"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                      {/* Export PDF — only for completed */}
                      {canExport(request.status) && onExportPDF && (
                        <button
                          onClick={() => onExportPDF(request)}
                          title="Export PDF"
                          className="p-2 bg-pink-50 hover:bg-pink-100 text-pink-600 border border-pink-200 rounded-lg transition-colors"
                        >
                          <FileDown className="w-4 h-4" />
                        </button>
                      )}
                      {/* Delete — only for draft */}
                      {canDelete(request.status) && onDeleteRequest && (
                        <button
                          onClick={() => onDeleteRequest(request)}
                          title="Delete"
                          className="p-2 bg-red-50 hover:bg-red-100 text-red-500 border border-red-200 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        totalItems={requests.length}
        itemsPerPage={ITEMS_PER_PAGE}
      />
    </div>
  );
};

export default RequestsTable;
